import React, { FC, useEffect, useCallback, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Text } from '@amzn/storm-ui';
import { useBundle } from '@amzn/react-arb-tools';
import { getActionEventTarget } from '@amzn/aac-chrome-api';
import useChrome from '../../shared/chrome/hooks/useChrome';
import { ChromeKeys } from '../../shared/chrome/enums';
import {
  useGetRequestContextQuery,
  useSubmitActivationRequestMutation,
  SubmitActivationRequestBody,
} from '@services/activation/activation';
import {
  ManagerRegistrationPageWrapper,
  ManagerRegistrationPageInnerWrapper,
} from '../managerAccountRegistration/style';
import {
  DSP_ACTIVATION_REQUEST_PAGE_ID,
  SUBMIT_ERROR_KEY,
  GENERIC_ERROR_KEY,
  CDFE_DEFAULT_VALUE,
  getTaxRequirements,
} from './constants';
import { countryCurrencyTimezoneMap } from 'src/shared/country-data';
import Settings from './components/Settings';
import Billing from './components/Billing';
import AgreementModal from './components/AgreementModal';

const SUBMIT_ACTION_ID = 'adspSubmitActivationRequest';
const CANCEL_ACTION_ID = 'adspCancelActivationRequest';
const AD_PROGRAM_ACCESS_PATH = '/am/managerAccounts';

export interface ActivationRequestFormValues {
  timezone: string;
  industry: string;
  partnerId: string;
  marketplace: string;
  requirePoNumber: boolean;
  companyName: string;
  contactName: string;
  contactEmail: string;
  phoneNumber: string;
  website: string;
  businessRegistrationNumber: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: string;
  state: string;
  stateName: string;
  vatNumber: string;
  gstNumber: string;
  cnpjNumber: string;
  rfcNumber: string;
  panNumber: string;
  cdfeNumber: string;
  pecEmail: string;
  kepEmail: string;
  mxTaxCode: string;
}

function buildSuccessUrl(entityId: string): string {
  return `${AD_PROGRAM_ACCESS_PATH}/${entityId}/ad-program-access`;
}

function buildTaxInformation(
  formData: ActivationRequestFormValues,
): Record<string, unknown> {
  // Only include sub-objects relevant to the current billing country /
  // marketplace, even if stale form state from a previously-selected country
  // has lingering values. react-hook-form keeps unmounted field values by
  // default, so a user who fills CNPJ for BR then switches to DE would
  // otherwise leak the CNPJ into the request body. Gating each branch on
  // `getTaxRequirements(formData.country)` mirrors what TaxFields.tsx renders
  // and what the backend's CountryTaxTranslator subclasses consume.
  //
  // Italy is the one exception: CDFE is required by the backend but legacy
  // UX lets users leave it blank, in which case we substitute the
  // well-known '0000000' placeholder. See CDFE_DEFAULT_VALUE.
  const requirements = getTaxRequirements(formData.country);
  const taxInformation: Record<string, unknown> = {};
  if (requirements.vat !== 'none' && formData.vatNumber) {
    taxInformation.vatTaxInformation = {
      registrationNumber: formData.vatNumber,
    };
  }
  if (requirements.gst !== 'none' && formData.gstNumber) {
    taxInformation.gstTaxInformation = {
      registrationNumber: formData.gstNumber,
    };
  }
  if (requirements.cnpj === 'required' && formData.cnpjNumber) {
    taxInformation.cnpjTaxInformation = {
      registrationNumber: formData.cnpjNumber,
    };
  }
  if (requirements.rfc === 'required' && formData.rfcNumber) {
    taxInformation.rfcTaxInformation = {
      registrationNumber: formData.rfcNumber,
    };
  }
  if (requirements.pan === 'required' && formData.panNumber) {
    taxInformation.panTaxInformation = {
      registrationNumber: formData.panNumber,
    };
  }
  if (requirements.cdfe === 'required') {
    taxInformation.cdfeTaxInformation = {
      registrationNumber: formData.cdfeNumber || CDFE_DEFAULT_VALUE,
    };
  }
  if (requirements.pec === 'required' && formData.pecEmail) {
    taxInformation.pecTaxInformation = {
      registrationNumber: formData.pecEmail,
    };
  }
  if (requirements.kep === 'optional' && formData.kepEmail) {
    taxInformation.kepTaxInformation = {
      registrationNumber: formData.kepEmail,
    };
  }
  if (formData.marketplace === 'MX' && formData.mxTaxCode) {
    taxInformation.mxRegimeTaxCode = {
      registrationNumber: formData.mxTaxCode,
    };
  }
  return taxInformation;
}

function buildSubmitBody(
  formData: ActivationRequestFormValues,
  entityId: string,
  principalObfuscatedId: string,
): SubmitActivationRequestBody {
  return {
    principalObfuscatedId,
    managerAccountId: entityId,
    companyProfile: {
      // Defaults match D16GAdspActivation-UIComponents DEFAULT_COMPANY_PROFILE:
      // https://code.amazon.com/packages/D16GAdspActivation-UIComponents/blobs/86f14f89ba37129e591a22c368ce20af415f7dc4/--/app/redux-rff/root-redux/constants.ts#L44-L52
      estimatedMonthlySpend: 0,
      size: 0,
      foundingDate: '',
      industry: formData.industry,
      partnerId: formData.partnerId || undefined,
    },
    activationRequest: {
      marketplace: {
        countryIsoCode: formData.marketplace,
        multiMarketplace: false,
      },
      companyName: formData.companyName,
      address: {
        line1: formData.addressLine1,
        line2: formData.addressLine2 || undefined,
        postalCode: formData.postalCode,
        city: formData.city,
        countryIsoCode: formData.country,
        state: formData.stateName || formData.state || '',
        stateIsoCode: formData.stateName
          ? formData.state || undefined
          : undefined,
      },
      contact: {
        name: formData.contactName,
        emails: formData.contactEmail,
        phoneNumber: formData.phoneNumber,
        website: formData.website,
        businessRegistrationNumber:
          formData.businessRegistrationNumber || undefined,
      },
      billingDetails: {
        mandatoryPurchaseOrderNumber: formData.requirePoNumber,
        // Country-conditional tax sub-fields populated from TaxFields.tsx.
        // Old ActivationService sends empty object: https://code.amazon.com/packages/D16GAdspActivation-UIComponents/blobs/86f14f89ba37129e591a22c368ce20af415f7dc4/--/app/redux-rff/root-redux/constants.ts#L24-L28
        taxInformation: buildTaxInformation(formData),
      },
      settings: {
        timeZone: formData.timezone,
        currency: countryCurrencyTimezoneMap.get(formData.marketplace)
          ?.currency,
        requirePoNumber: formData.requirePoNumber,
      },
    },
  };
}

const DspActivationRequestPage: FC = () => {
  const [intl, isBundleLoading] = useBundle('pages.dspActivationRequest');
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get('entityId') || '';
  const {
    data: context,
    isLoading,
    isError,
  } = useGetRequestContextQuery({ entityId }, { skip: !entityId });
  const [submitRequest, { isLoading: isSubmitting }] =
    useSubmitActivationRequestMutation();
  const [error, setError] = useState<string | null>(null);
  const [showAgreement, setShowAgreement] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const contextRef = useRef(context);
  const pendingData = useRef<ActivationRequestFormValues | null>(null);
  contextRef.current = context;

  useChrome(ChromeKeys.DspActivationRequest);

  const methods = useForm<ActivationRequestFormValues>({
    defaultValues: {
      timezone: '',
      industry: '',
      partnerId: '',
      marketplace: '',
      requirePoNumber: false,
      companyName: '',
      contactName: '',
      contactEmail: '',
      phoneNumber: '',
      website: '',
      businessRegistrationNumber: '',
      addressLine1: '',
      addressLine2: '',
      postalCode: '',
      city: '',
      country: '',
      state: '',
      stateName: '',
      vatNumber: '',
      gstNumber: '',
      cnpjNumber: '',
      rfcNumber: '',
      panNumber: '',
      cdfeNumber: '',
      pecEmail: '',
      kepEmail: '',
      mxTaxCode: '',
    },
  });

  const doSubmit = useCallback(
    async (formData: ActivationRequestFormValues) => {
      if (isSubmitting) return;
      if (!contextRef.current?.principalObfuscatedId) return;
      setError(null);
      try {
        const body = buildSubmitBody(
          formData,
          entityId,
          contextRef.current.principalObfuscatedId,
        );
        const result = await submitRequest({ entityId, body }).unwrap();
        window.location.href = result.successUrl;
      } catch {
        setError(intl.getMessage(SUBMIT_ERROR_KEY));
      }
    },
    [isSubmitting, submitRequest, entityId, intl],
  );

  // Chrome submit button → validate form → if valid, open agreement modal
  const onSubmit = useCallback(
    (formData: ActivationRequestFormValues) => {
      if (!contextRef.current?.adspAgreementDocuments?.length) {
        doSubmit(formData);
        return;
      }
      pendingData.current = formData;
      setShowAgreement(true);
    },
    [doSubmit],
  );

  // "I agree" in modal → submit with the already-validated data
  const onAcceptAgreement = useCallback(() => {
    setShowAgreement(false);
    if (pendingData.current) doSubmit(pendingData.current);
  }, [doSubmit]);

  useEffect(() => {
    const actionEventTarget = getActionEventTarget();
    const handleSubmit = () => formRef.current?.requestSubmit();
    const handleCancel = () => {
      const maId = contextRef.current?.managerAccountId;
      window.location.href = maId ? buildSuccessUrl(maId) : '/';
    };

    actionEventTarget.addEventListener(SUBMIT_ACTION_ID, handleSubmit);
    actionEventTarget.addEventListener(CANCEL_ACTION_ID, handleCancel);

    return () => {
      actionEventTarget.removeEventListener(SUBMIT_ACTION_ID, handleSubmit);
      actionEventTarget.removeEventListener(CANCEL_ACTION_ID, handleCancel);
    };
  }, []);

  useEffect(() => {
    if (context?.redirectUrl) {
      window.location.href = context.redirectUrl;
    }
  }, [context?.redirectUrl]);

  if (isLoading || isBundleLoading) {
    return null;
  }

  if (isError || !entityId || !context) {
    return <Text>{intl.getMessage(GENERIC_ERROR_KEY)}</Text>;
  }

  if (context.redirectUrl) {
    return null;
  }

  return (
    <ManagerRegistrationPageWrapper
      id={DSP_ACTIVATION_REQUEST_PAGE_ID}
      data-testid={DSP_ACTIVATION_REQUEST_PAGE_ID}
    >
      <ManagerRegistrationPageInnerWrapper>
        <FormProvider {...methods}>
          <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
            <Settings intl={intl} context={context} />
            <Billing intl={intl} context={context} />
            {error && <Text>{error}</Text>}
          </form>
        </FormProvider>
        <AgreementModal
          intl={intl}
          isOpen={showAgreement}
          agreements={context.adspAgreementDocuments || []}
          onClose={() => setShowAgreement(false)}
          onAccept={onAcceptAgreement}
        />
      </ManagerRegistrationPageInnerWrapper>
    </ManagerRegistrationPageWrapper>
  );
};

export default DspActivationRequestPage;
